import * as Papa from 'papaparse'
import { sanitizeForCSV } from 'shared/utils/csvExport'
import { getColumnDataKey, getColumnId, isColumnWithAccessorFn } from '../components/table/utils'
import { ROW_SELECTION_COLUMN_ID } from '../components/table/constants'
import type { ChartCardTableColumnDef } from '../components/table/tanstack'
import type { ChartDataRow, ChartDataValue } from '../types'

const flattenLeafColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
): Array<ChartCardTableColumnDef<DataKey, DataRow>> => {
  const leaves: Array<ChartCardTableColumnDef<DataKey, DataRow>> = []

  for (const column of columns) {
    if ('columns' in column && Array.isArray(column.columns) && column.columns.length > 0) {
      leaves.push(...flattenLeafColumns(column.columns))
      continue
    }
    leaves.push(column)
  }

  return leaves
}

const getExportableColumns = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
): Array<ChartCardTableColumnDef<DataKey, DataRow>> =>
  flattenLeafColumns(columns).filter((column) => getColumnId(column) !== ROW_SELECTION_COLUMN_ID)

const getColumnHeaderLabel = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>
): string => {
  const metaLabel = column.meta?.chartCard?.label
  if (typeof metaLabel === 'string' && metaLabel.length > 0) {
    return metaLabel
  }
  if (typeof column.header === 'string' && column.header.length > 0) {
    return column.header
  }
  return getColumnId(column)
}

const cellValueToCsvField = (value: ChartDataValue | null | undefined): string | number => {
  if (value == null) {
    return ''
  }
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : ''
  }
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE'
  }

  return sanitizeForCSV(value)
}

const getCellValue = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>(
  column: ChartCardTableColumnDef<DataKey, DataRow>,
  row: DataRow,
  rowIndex: number
): ChartDataValue | null | undefined => {
  if (isColumnWithAccessorFn(column)) {
    return column.accessorFn(row, rowIndex)
  }

  const dataKey = getColumnDataKey(column)
  if (dataKey != null) {
    return row[dataKey]
  }

  return undefined
}

/**
 * Build a CSV string from ChartCard table columns and row data.
 * Nested group columns are flattened to leaves; the row-selection column is omitted.
 */
export const buildTableCsvContent = <DataKey extends string, DataRow extends ChartDataRow<DataKey>>({
  columns,
  data
}: {
  columns: Array<ChartCardTableColumnDef<DataKey, DataRow>>
  data: DataRow[]
}): string => {
  const exportableColumns = getExportableColumns(columns)
  if (exportableColumns.length === 0 || data.length === 0) {
    return ''
  }

  const headers = exportableColumns.map((column) => getColumnHeaderLabel(column))
  const rows = data.map((row, rowIndex) =>
    exportableColumns.map((column) => cellValueToCsvField(getCellValue(column, row, rowIndex)))
  )

  return Papa.unparse([headers, ...rows], {
    delimiter: ',',
    quotes: true
  })
}
